import React, { useEffect, useState } from 'react'
import { Alert, Button, Drawer, Flex, Result, Spin, Typography } from 'antd'
import { Link } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { QuestionsList } from '../../campaign/components/questions-list'
import { Question } from '../../campaign/input/store-input'
import { RegenerateQuestionsButton } from '../../components/regenerate-questions-button'
import { useCreateScreening, useSuggestQuestions, useWhatsapp } from '../hooks'
import { SiteVacancy, WhatsappSettings } from '../site'

type ScreeningDrawerProps = {
    siteId: string
    vacancy: SiteVacancy
    onClose: () => void
}

type ValidationErrorResponse = {
    message?: string
    errors?: Record<string, string[]>
}

function errorText(error: unknown, fallback: string): string {
    if (isAxiosError<ValidationErrorResponse>(error)) {
        const errors = Object.values(error.response?.data?.errors ?? {}).flat()

        return errors[0] ?? error.response?.data?.message ?? fallback
    }

    return fallback
}

/**
 * Switches on WhatsApp screening for a vacancy: Evaa writes questions from the vacancy text, the user reviews them.
 * The same flow as "Create agent" in wp-admin.
 */
export function ScreeningDrawer({ siteId, vacancy, onClose }: ScreeningDrawerProps) {
    const { t } = useTranslation()
    const { data: whatsapp, isLoading } = useWhatsapp(siteId)
    const suggest = useSuggestQuestions(siteId, vacancy.id)
    const create = useCreateScreening(siteId, vacancy.id)
    const [open, setOpen] = useState(true)
    const [questions, setQuestions] = useState<Question[]>([])
    const configured = isWhatsappConfigured(whatsapp)

    const apply = (result: { questions: string[] }) =>
        setQuestions(
            result.questions.map((text) => ({
                text,
                autoReject: false,
            }))
        )

    useEffect(() => {
        if (configured) {
            suggest.mutate({}, { onSuccess: apply })
        }
        // Only once, when the WhatsApp settings are known.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configured])

    const regenerate = (feedback: string) =>
        suggest
            .mutateAsync({
                feedback,
                previousQuestions: questions.map((question) => question.text),
            })
            .then(apply)

    const close = () => setOpen(false)
    const canSwitchOn = configured && !suggest.isPending && questions.length > 0

    const footer = (
        <Flex
            justify="end"
            gap="small"
        >
            <Button onClick={close}>{t('Cancel')}</Button>
            <Button
                type="primary"
                disabled={!canSwitchOn}
                loading={create.isPending}
                onClick={() => create.mutate(questions)}
            >
                {t('Switch on')}
            </Button>
        </Flex>
    )

    const renderContent = () => {
        if (create.data) {
            return (
                <ScreeningSwitchedOn
                    vacancy={vacancy}
                    screening={create.data}
                    onClose={close}
                />
            )
        }

        if (isLoading) {
            return <Spin />
        }

        if (!configured) {
            return <WhatsappNotConfigured siteId={siteId} />
        }

        return (
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Evaa wrote these questions from the vacancy text and asks them in the WhatsApp conversation. Review the questions and mark answers that should lead to rejection.'
                    )}
                </Typography.Text>
                {suggest.isError && (
                    <Alert
                        type="error"
                        showIcon
                        message={errorText(suggest.error, t('No questions could be written. Please try again.'))}
                    />
                )}
                {create.isError && (
                    <Alert
                        type="error"
                        showIcon
                        message={errorText(create.error, t('Applying via WhatsApp could not be switched on.'))}
                    />
                )}
                {suggest.isPending && questions.length === 0 ? (
                    <WritingQuestions />
                ) : (
                    <section aria-label={t('Questions')}>
                        <Flex
                            justify="space-between"
                            align="center"
                            gap="small"
                            wrap
                            className="mb-2"
                        >
                            <Typography.Text strong>{t('Questions')}</Typography.Text>
                            <RegenerateQuestionsButton
                                onRegenerate={regenerate}
                                loading={suggest.isPending}
                            />
                        </Flex>
                        <Spin spinning={suggest.isPending}>
                            <QuestionsList
                                value={questions}
                                onChange={setQuestions}
                                max={15}
                                maxLength={500}
                            />
                        </Spin>
                    </section>
                )}
            </Flex>
        )
    }

    return (
        <Drawer
            open={open}
            title={create.data ? undefined : t('Applying via WhatsApp for "{{title}}"', { title: vacancy.title })}
            onClose={close}
            afterOpenChange={(visible) => !visible && onClose()}
            maskClosable={false}
            width={680}
            footer={create.data ? null : footer}
        >
            {renderContent()}
        </Drawer>
    )
}

function isWhatsappConfigured(whatsapp: WhatsappSettings | undefined): boolean {
    if (!whatsapp) {
        return false
    }

    const { whatsappBusinessAccountId, phoneNumberId, startMessageTemplateId } = whatsapp.selected

    return Boolean(whatsappBusinessAccountId && phoneNumberId && startMessageTemplateId)
}

function WritingQuestions() {
    const { t } = useTranslation()

    return (
        <Flex
            gap="small"
            align="center"
        >
            <Spin size="small" />
            <Typography.Text>{t('Writing questions from the vacancy… (about 15 seconds)')}</Typography.Text>
        </Flex>
    )
}

function WhatsappNotConfigured({ siteId }: { siteId: string }) {
    const { t } = useTranslation()

    return (
        <Alert
            type="info"
            showIcon
            message={t('Choose the WhatsApp number and start message first.')}
            action={
                <Link
                    to="/websites/$siteId/settings/whatsapp"
                    params={{ siteId }}
                >
                    <Button size="small">{t('Open settings')}</Button>
                </Link>
            }
        />
    )
}

type ScreeningSwitchedOnProps = {
    vacancy: SiteVacancy
    screening: SiteVacancy & { publishingIssues: string[] }
    onClose: () => void
}

function ScreeningSwitchedOn({ vacancy, screening, onClose }: ScreeningSwitchedOnProps) {
    const { t } = useTranslation()
    const hasIssues = screening.publishingIssues.length > 0

    const subTitle = hasIssues
        ? t('The apply form is not published yet: {{issues}}', {
              issues: screening.publishingIssues.join(' '),
          })
        : t('The apply buttons on the vacancy now open the Evaa form. Candidates continue on WhatsApp.')

    return (
        <Result
            status={hasIssues ? 'warning' : 'success'}
            title={t('Applying via WhatsApp is on for "{{title}}"', { title: vacancy.title })}
            subTitle={subTitle}
            extra={[
                <Link
                    key="settings"
                    to="/campaigns/$campaignId/settings"
                    params={{ campaignId: screening.campaignId ?? '' }}
                >
                    <Button type="primary">{t('WhatsApp conversation settings')}</Button>
                </Link>,
                <Button
                    key="close"
                    onClick={onClose}
                >
                    {t('Close')}
                </Button>,
            ]}
        />
    )
}
